import type { FC } from 'react';
import { useSelector } from 'react-redux';

import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { AdjustedResidualItem } from '@makemydeal/dr-offer-redux';

import { ADJUSTED_RESIDUAL_LABEL } from '../../constants';
import { useIsColumnCalculated } from './hooks/useIsColumnCalculated';
import RowLabelCell from './RowLabelCell';
import { CompactGridItem, StyledTypography } from './styled.components';
import { disabledStyle } from './utils';

const NO_VALUE = '—';

type AdjustedResidualsProps = {
    isLoading?: boolean;
};

const AdjustedResiduals: FC<AdjustedResidualsProps> = ({ isLoading }) => {
    const adjustedResiduals: AdjustedResidualItem[] = useSelector(offerReduxSelectors.getPaymentGridAdjustedResiduals);
    const columnView = useSelector(offerReduxSelectors.getPaymentGridColumnView);

    const isColumnLoading = (month: number | null) =>
        columnView.isMasked ? month != null && columnView.loadingTerms.has(month) : Boolean(isLoading);

    const isCalculatedColumn = useIsColumnCalculated();
    const isColumnCalculated = (month: number | null, index: number) => isCalculatedColumn(index, month, !isColumnLoading(month));

    return (
        <>
            <RowLabelCell
                label={ADJUSTED_RESIDUAL_LABEL}
                bordered={true}
                cell={CompactGridItem}
                unborderedMarginTop="0.4rem"
                testId="adjusted-residual-label"
            />
            {adjustedResiduals.map((item, index) => (
                <CompactGridItem
                    xs={3}
                    className={' payment-grid-item'}
                    key={`adjusted-residual-${index}`}
                    paddingTop="0"
                    data-testid={`adjusted-residual-${index}`}
                    $isCalculated={isColumnCalculated(item.month, index)}
                >
                    <StyledTypography
                        className="large"
                        data-testid={`adjusted-residual-value-${index}`}
                        style={{ color: 'black', textAlign: 'left', ...(isColumnLoading(item.month) && disabledStyle) }}
                    >
                        {item.amount == null ? NO_VALUE : formatDollarsAndCents(item.amount)}
                    </StyledTypography>
                </CompactGridItem>
            ))}
        </>
    );
};

export default AdjustedResiduals;
